import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { api } from '../../config/api';
import { getClientDetails as fetchAllClients } from '../../services/clientService';
import { getDecryptedData } from '../../utils/authStorage';

export function buildClientOptionValue(clientId, clientName) {
  return JSON.stringify({
    ClientId: clientId ? String(clientId) : '',
    ClientName: clientName || '',
  });
}

export function resolveSelectedClientOption(clients, clientId, clientName) {
  const normalizedId = clientId ? String(clientId) : '';
  const normalizedName = String(clientName || '').trim();

  if (normalizedId) {
    const byId = clients.find((item) => String(item._id) === normalizedId);
    if (byId) {
      return buildClientOptionValue(byId._id, byId.ClientName);
    }
  }

  if (normalizedName) {
    const byName = clients.find(
      (item) => String(item.ClientName || '').trim().toLowerCase() === normalizedName.toLowerCase()
    );
    if (byName) {
      return buildClientOptionValue(byName._id, byName.ClientName);
    }
  }

  if (normalizedId || normalizedName) {
    return buildClientOptionValue(normalizedId, normalizedName);
  }

  return buildClientOptionValue('', '');
}

function RequirementClientSelect({
  mode = 'admin',
  clientId = '',
  clientName = '',
  onChange,
  id = 'requirement-client-select',
  style,
  required = true,
}) {
  const [clients, setClients] = useState([]);
  const userId = getDecryptedData('Id');

  const loadClients = useCallback(async () => {
    try {
      if (mode === 'teamlead') {
        if (!userId) {
          setClients([]);
          return;
        }
        const { data } = await api.get(`/TlClients/${userId}`);
        setClients(Array.isArray(data.Client) ? data.Client : []);
        return;
      }

      const data = await fetchAllClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load clients for requirement:', error);
      setClients([]);
    }
  }, [mode, userId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const selectValue = useMemo(
    () => resolveSelectedClientOption(clients, clientId, clientName),
    [clients, clientId, clientName]
  );

  const selectedOption = useMemo(() => {
    try {
      return JSON.parse(selectValue);
    } catch {
      return { ClientId: '', ClientName: '' };
    }
  }, [selectValue]);

  const showLegacyOption = Boolean(
    selectedOption.ClientName
    && !clients.some((item) => String(item._id) === String(selectedOption.ClientId))
    && !clients.some(
      (item) => String(item.ClientName || '').trim().toLowerCase()
        === String(selectedOption.ClientName).trim().toLowerCase()
    )
  );

  const handleChange = (event) => {
    try {
      const parsed = JSON.parse(event.target.value);
      onChange?.({
        clientId: parsed.ClientId ? String(parsed.ClientId) : '',
        client: parsed.ClientName || '',
      });
    } catch (error) {
      console.error('Invalid client selection:', error);
    }
  };

  return (
    <Form.Select
      id={id}
      value={selectValue}
      onChange={handleChange}
      required={required}
      style={style}
    >
      <option value={buildClientOptionValue('', '')}>Select Client</option>
      {showLegacyOption && (
        <option value={selectValue}>
          {selectedOption.ClientName}
          {' '}
          (current)
        </option>
      )}
      {clients.map((item) => (
        <option
          key={item._id}
          value={buildClientOptionValue(item._id, item.ClientName)}
        >
          {item.ClientName}
        </option>
      ))}
    </Form.Select>
  );
}

export default RequirementClientSelect;
